import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { CheckCircle, Send, MessageCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabaseClient';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isConfiguring, setIsConfiguring] = useState(true);
  const telegramUrl = 'https://t.me/ratchagananalysis';
  const whatsappUrl = 'https://wa.me/919663306819?text=Hi!%20I%20just%20enrolled%20in%20the%20course.';

  useEffect(() => {
    const finalizeTransaction = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlCourseId = urlParams.get('courseId');
      const status = localStorage.getItem('payment_status');
      const pendingPaymentId = localStorage.getItem('pending_payment_id');
      const pendingPlanId = localStorage.getItem('pending_plan_id');

      if (status !== 'success' && !urlCourseId) {
        if (!user) {
          navigate('/login');
          return;
        }
        navigate('/courses', { replace: true });
        return;
      }

      try {
        // 1. Update payment status to paid
        if (pendingPaymentId) {
          await supabase
            .from('payments')
            .update({ status: 'paid' })
            .eq('id', pendingPaymentId);
        }

        // 2. Fetch plan to get course_id
        if (urlCourseId && user) {
          // Direct enrollment from URL
          const { data: existing } = await supabase
            .from('enrollments')
            .select('id')
            .eq('user_id', user.id)
            .eq('course_id', urlCourseId)
            .maybeSingle();

          if (!existing) {
            await supabase
              .from('enrollments')
              .upsert({
                user_id: user.id,
                course_id: urlCourseId,
                status: 'active'
              }, { onConflict: 'user_id,course_id' });
          }
        } else if (pendingPlanId && user) {
          const { data: planData } = await supabase
            .from('plans')
            .select('course_id, plan_key')
            .eq('id', pendingPlanId)
            .single();

          if (planData?.course_id) {
            const { data: existingEnrollment } = await supabase
              .from('enrollments')
              .select('id')
              .eq('user_id', user.id)
              .eq('course_id', planData.course_id)
              .maybeSingle();

            if (!existingEnrollment) {
              await supabase
                .from('enrollments')
                .upsert({
                  user_id: user.id,
                  course_id: planData.course_id,
                  plan_key: planData.plan_key,
                  payment_id: pendingPaymentId,
                  status: 'active',
                  enrolled_at: new Date().toISOString()
                }, { onConflict: 'user_id,course_id' });
            }
          }
        }
      } catch (error) {
        console.error('Finalization error:', error);
      } finally {
        setIsConfiguring(false);
        // We keep status for the component to render, but clear the sensitive pending IDs
        localStorage.removeItem('pending_payment_id');
        localStorage.removeItem('pending_plan_id');
      }
    };

    finalizeTransaction();

    return () => {
      localStorage.removeItem('payment_status');
    };
  }, [navigate, user]);

  if (isConfiguring) {
    return (
      <Layout>
        <div className="section-padding min-h-[80vh] flex items-center justify-center">
          <p className="text-muted-foreground animate-pulse font-black uppercase tracking-widest text-[10px]">Configuring Your Trading Terminal...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="section-padding min-h-[80vh] flex items-center">
        <div className="container-max max-w-2xl text-center">
          {/* Success Icon */}
          <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8 animate-scale-in">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>

          <h1 className="heading-lg text-primary mb-4">
            🎉 Congratulations!
          </h1>

          <p className="text-xl text-muted-foreground mb-8">
            You have successfully joined the <span className="font-semibold text-primary">Ratchagan Analysis</span> trading community. Your access is now live!
          </p>

          {/* Next Steps */}
          <div className="card-premium border border-border mb-8">
            <h2 className="text-lg font-semibold text-primary mb-4">Immediate Actions</h2>
            <p className="text-muted-foreground mb-6">
              Join our community clusters to receive real-time market updates and peer support.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <a
                href={telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 p-4 bg-[#0088cc]/10 rounded-xl hover:bg-[#0088cc]/20 transition-colors group"
              >
                <Send className="w-6 h-6 text-[#0088cc]" />
                <div className="text-left">
                  <p className="font-medium text-primary">Join Telegram</p>
                  <p className="text-xs text-muted-foreground">Daily Analyis</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#0088cc] opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 p-4 bg-[#25D366]/10 rounded-xl hover:bg-[#25D366]/20 transition-colors group"
              >
                <MessageCircle className="w-6 h-6 text-[#25D366]" />
                <div className="text-left">
                  <p className="font-medium text-primary">Join WhatsApp</p>
                  <p className="text-xs text-muted-foreground">Live Signal Flow</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#25D366] opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>

          {/* What's Next */}
          <div className="bg-secondary/50 rounded-xl p-6 mb-8 text-left">
            <h3 className="font-semibold text-primary mb-4">Security Credentials Verified</h3>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                Course enrollment synchronized with database
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                Interactive player access activated
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                Downloadable resources available in player
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/my-courses">
              <Button className="btn-gold h-16 px-10 text-[10px] font-black uppercase tracking-[0.2em] shadow-premium group">
                ENTER MY COURSES
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="h-16 px-10 text-[10px] font-black uppercase tracking-[0.2em] border-primary/10 hover:bg-primary/5">
                Market Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PaymentSuccess;
